import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import CampoTexto from '@/components/base/CampoTexto';
import { ehPdv } from './utils';
import type { CaixaDoEvento, FichaPor, TerminalCaixa } from './types';

interface Props {
  eventoId: number;
  caixa: CaixaDoEvento | null;
  terminais: TerminalCaixa[];
  aoFechar: () => void;
  aoSalvo: () => void;
}

function Interruptor({
  ligado,
  rotulo,
  aoAlternar,
}: {
  ligado: boolean;
  rotulo: string;
  aoAlternar: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      onClick={() => aoAlternar(!ligado)}
      className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors ${
        ligado ? 'bg-primary-500' : 'bg-secondary-300'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
          ligado ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// Folha para criar/editar um caixa (nome, maquininha, fichas e modo).
export default function ModalCaixa({ eventoId, caixa, terminais, aoFechar, aoSalvo }: Props) {
  const [nome, setNome] = useState(caixa?.nome ?? '');
  const [terminal, setTerminal] = useState<number | null>(caixa?.terminal ?? null);
  const [imprime, setImprime] = useState(caixa?.imprime_fichas ?? true);
  const [fichaPor, setFichaPor] = useState<FichaPor>(caixa?.ficha_por ?? 'unidade');
  const [ativo, setAtivo] = useState(caixa?.ativo ?? true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const terminalEscolhido = terminais.find((t) => t.terminal === terminal) ?? null;
  const podeImprimir = terminalEscolhido?.imprime === true;

  function escolherTerminal(t: TerminalCaixa | null) {
    setTerminal(t ? t.terminal : null);
    if (t && t.imprime) setImprime(true);
    if (!t || !t.imprime) setImprime(false);
  }

  async function salvar() {
    if (!nome.trim()) {
      setErro('Dê um nome ao caixa.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('caixa_salvar_caixa', {
        p_evento: eventoId,
        p_nome: nome.trim(),
        p_caixa: caixa ? caixa.caixa_id : null,
        p_terminal: terminal,
        p_imprime_fichas: imprime,
        p_ficha_por: fichaPor,
        p_ativo: ativo,
      });
      if (error) {
        // A função já devolve frases em português — mostra a mensagem real do banco.
        setErro(mensagemAmigavel(error, 'Não foi possível salvar. Tente de novo.'));
        return;
      }
      mostrarToast('Caixa salvo', 3000, 'sucesso');
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  function linhaTerminal(t: TerminalCaixa) {
    const selecionado = terminal === t.terminal;
    const habilitado = ehPdv(t);
    return (
      <button
        key={t.terminal_externo}
        type="button"
        disabled={!habilitado}
        onClick={() => escolherTerminal(t)}
        className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
          selecionado
            ? 'border-primary-500 bg-primary-50'
            : 'border-secondary-200 bg-white hover:bg-background-50'
        } ${habilitado ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
      >
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold text-foreground-950">{t.apelido}</p>
          {!habilitado && (
            <p className="mt-0.5 text-[13px] text-warning-700">ligue ao sistema primeiro</p>
          )}
        </div>
        {selecionado && (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
            <i className="ri-check-line text-xl" />
          </span>
        )}
      </button>
    );
  }

  return (
    <Modal
      aberto
      titulo={caixa ? 'Editar caixa' : 'Novo caixa'}
      aoFechar={aoFechar}
      rodape={
        <div className="space-y-2">
          <Botao
            variante="primario"
            tamanho="lg"
            cheio
            carregando={salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Botao>
          <Botao variante="secundario" tamanho="md" cheio onClick={aoFechar}>
            Cancelar
          </Botao>
        </div>
      }
    >
      <div className="space-y-5">
        <CampoTexto
          id="caixa-nome"
          name="nome"
          label="Nome"
          help="Ex.: Caixa Central."
          value={nome}
          onChange={(v) => {
            setNome(v);
            if (erro) setErro(null);
          }}
          required
          error={erro}
          autoFocus
          placeholder="Caixa Central"
        />

        <div>
          <p className="text-[15px] font-medium text-foreground-950">Maquininha</p>
          <p className="mt-1 text-[13px] text-foreground-500">
            Escolha qual maquininha recebe as vendas de cartão deste caixa.
          </p>
          <div className="mt-3 space-y-2">
            <button
              type="button"
              onClick={() => escolherTerminal(null)}
              className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                terminal === null
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-secondary-200 bg-white hover:bg-background-50'
              }`}
            >
              <span className="text-[15px] font-semibold text-foreground-950">
                Sem maquininha (fichas de papel)
              </span>
              {terminal === null && (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                  <i className="ri-check-line text-xl" />
                </span>
              )}
            </button>
            {terminais.map(linhaTerminal)}
          </div>
        </div>

        {podeImprimir && (
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[15px] font-medium text-foreground-950">
                Imprimir fichas na maquininha
              </p>
              <p className="mt-1 text-[13px] text-foreground-500">
                As fichas saem impressas assim que a venda é fechada.
              </p>
            </div>
            <Interruptor ligado={imprime} rotulo="Imprimir fichas" aoAlternar={setImprime} />
          </div>
        )}

        <div>
          <p className="text-[15px] font-medium text-foreground-950">Uma ficha para cada…</p>
          <div className="mt-2 inline-flex gap-1 rounded-full bg-secondary-100 p-1">
            {(
              [
                { valor: 'unidade' as FichaPor, rotulo: 'item' },
                { valor: 'barraca' as FichaPor, rotulo: 'barraca' },
              ]
            ).map((op) => (
              <button
                key={op.valor}
                type="button"
                onClick={() => setFichaPor(op.valor)}
                className={`h-9 cursor-pointer whitespace-nowrap rounded-full px-4 text-[14px] font-semibold transition-colors ${
                  fichaPor === op.valor
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-700 hover:bg-white'
                }`}
              >
                {op.rotulo}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[13px] text-foreground-500">
            Item: 10 cervejas = 10 fichas. Barraca: uma ficha com a lista da barraca.
          </p>
        </div>

        {caixa && (
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[15px] font-medium text-foreground-950">Caixa ativo</p>
              <p className="mt-1 text-[13px] text-foreground-500">
                Desligado, o caixa não aparece para o operador abrir.
              </p>
            </div>
            <Interruptor ligado={ativo} rotulo="Caixa ativo" aoAlternar={setAtivo} />
          </div>
        )}
      </div>
    </Modal>
  );
}